import _ from 'lodash';
import { IngredientMatchGetDto } from 'svila-common';
import { IngredientGetDto } from '~/src/types/ingredientGetDto';
import { IngredientCombinationType, IngredientProduct, Nutrients } from '~/src/types/Misc';
import { NutritionSetEditDto } from '~/src/types/nutritionSetEditDto';
import { DEFAULT_SERVING_SIZE } from '~/src/utils/constants';
import { calcValueOfPercentage } from '~/src/utils/misc';

interface ReturnType {
  combinations: number[][];
}

class NutrientService {
  static interval = 0.5;
  allCombinations: IngredientCombinationType[];
  targetNutrients: Nutrients;
  bestCombinations: number[][];
  bestCombinationError: number;
  ingredient: IngredientGetDto;

  constructor(ingredient: IngredientGetDto) {
    const nutritionSet = { ...ingredient.nutritionSet } as NutritionSetEditDto;

    if (Object.prototype.hasOwnProperty.call(nutritionSet, 'id')) {
      delete nutritionSet.id;
    }

    const childIngredients = ingredient.childIngredients.map((ingr) => {
      const { id, ...restNutritionSet } = ingr.nutritionSet;
      const nutritionSet = NutrientService.nutritionSetTo100gram(
        restNutritionSet,
        ingr.servingSize || DEFAULT_SERVING_SIZE,
      );
      return {
        ...ingr,
        nutritionSet,
      };
    });

    this.ingredient = { ...ingredient, childIngredients };
    this.targetNutrients = NutrientService.nutritionSetTo100gram(
      nutritionSet,
      ingredient.servingSize || DEFAULT_SERVING_SIZE,
    );
    this.bestCombinations = [];
    this.allCombinations = [];
    this.bestCombinationError = 100000000;
  }

  /**
   * Calculate all errors from all combinations by using recursivity.
   */
  calculateRecursive(): IngredientCombinationType {
    const firstCombo = this.getFirstCombination(this.ingredient.childIngredients);
    const errorScore = this.calculateErrorForCombination(
      firstCombo,
      this.targetNutrients,
      this.ingredient.childIngredients,
    );
    this.bestCombinationError = errorScore;
    this.bestCombinations.push(firstCombo);
    this.loopCombinations([], this.ingredient.childIngredients.length);
    const result = this.numberCombination2IngredientCombinationType(
      this.bestCombinations[0],
      this.bestCombinationError,
    );
    return result;
  }

  calculateErrorForCombination(
    combination: number[],
    targetNutrients: NutritionSetEditDto,
    childIngredients: IngredientGetDto[],
  ): number {
    const errorObject = {} as NutritionSetGetDto;
    Object.keys(targetNutrients).forEach((targetNutrient: Nutrients) => {
      if (targetNutrients?.[targetNutrient]) {
        let sumOfNutrient = 0;

        for (let i = 0; i < childIngredients.length; i++) {
          const ingredientQ = childIngredients[i];
          sumOfNutrient += ((ingredientQ.nutritionSet[targetNutrient] ?? 0) * combination[i]) / 100;
        }

        errorObject[targetNutrient] =
          Math.abs(targetNutrients[targetNutrient] - sumOfNutrient) / targetNutrients[targetNutrient];
      }
    });
    const errorScore = this.calculateErrorScore(errorObject);
    return errorScore;
  }

  calculateErrorScore(errorObject: Nutrients): number {
    let sum = 0;

    Object.keys(errorObject).forEach((nutrient: string) => {
      sum += errorObject[nutrient]; // later we should probably weight it;
    });
    return Math.abs(sum);
  }

  /**
   * Get the first combination from ingredients we have. Array of quantities.
   * @returns number[]
   */
  getFirstCombination(childIngredients: IngredientGetDto[]): number[] {
    const combination: number[] = [];
    for (let i = childIngredients.length - 1; i >= 0; i--) {
      const im = childIngredients[i];
      if (i === 0) {
        const totalOthers = (childIngredients.length - 1) * NutrientService.interval;
        const thisItem = childIngredients[0]?.moreThan ?? 100 - totalOthers;
        combination.unshift(thisItem);
      } else {
        combination.unshift(NutrientService.interval);
      }
    }
    return combination;
  }

  loopCombinations(parents: number[], childrenCount: number, depth = 1): void {
    // console.log('>'.repeat(depth), parents, childrenCount);
    let totalOfParents = 0;
    for (let p = 0; p < parents.length; p++) {
      totalOfParents += parents[p];
    }
    const definedMin =
      this.ingredient.childIngredients[this.ingredient.childIngredients.length - childrenCount]?.lessThan;
    const definedMax =
      this.ingredient.childIngredients[this.ingredient.childIngredients.length - childrenCount]?.moreThan;
    const min = Math.ceil((100 - totalOfParents) / childrenCount); // .floor?
    const maxFromSum = 100 - (totalOfParents + (childrenCount - 1) * NutrientService.interval);
    const max = parents.length > 0 ? Math.min(maxFromSum, parents[parents.length - 1]) : maxFromSum;
    const finalMin = Number.isInteger(definedMin) && definedMin >= min && definedMin >= 0 ? definedMin : min;
    const finalMax = Number.isInteger(definedMax) && definedMax <= max && definedMax > 0 ? definedMax : max;
    let i = finalMax;

    while (i >= finalMin) {
      const newChildrenCount = childrenCount - 1;
      const newParents = [...parents, i];

      if (newChildrenCount <= 0) {
        const errorScore = this.calculateErrorForCombination(
          newParents,
          this.targetNutrients,
          this.ingredient.childIngredients,
        );
        if (errorScore < this.bestCombinationError) {
          this.bestCombinationError = errorScore;
          this.bestCombinations = [newParents];
        } else if (errorScore === this.bestCombinationError) {
          this.bestCombinations.push(newParents);
        }
      } else {
        this.loopCombinations(newParents, newChildrenCount, depth + 1);
      }
      i -= NutrientService.interval;
    }
  }

  numberCombination2IngredientCombinationType(combination: number[], error: number): IngredientCombinationType {
    const ingredients = [];
    for (let i = 0; i < combination.length; i++) {
      const iq = {
        quantity: combination[i],
        ingredient: this.ingredient.childIngredients[i],
      };
      ingredients.push(iq);
    }
    const ict: IngredientCombinationType = {
      errorScore: error,
      ingredients,
    };
    return ict;
  }

  static extractNutrientsFromLegacyAndFoundation(legacyFood: SearchResultFood): Nutrients {
    const nutrientResult = {} as Nutrients;
    legacyFood?.foodNutrients?.forEach((nutrientObject) => {
      switch (nutrientObject.nutrientId) {
        case 1008: // 1062 is in KJ
          nutrientResult.calories = nutrientObject.value ?? null;
          break;
        case 1004:
          nutrientResult.fat = nutrientObject.value ?? null;
          break;
        case 1258:
          nutrientResult.saturatedFat = nutrientObject.value ?? null;
          break;
        case 1257:
          nutrientResult.transFat = nutrientObject.value ?? null;
          break;
        case 1253:
          nutrientResult.cholesterol = nutrientObject.value ?? null;
          break;
        case 1093:
          nutrientResult.sodium = nutrientObject.value ?? null;
          break;
        case 1005:
          nutrientResult.carbohydrates = nutrientObject.value ?? null;
          break;
        case 1079:
          nutrientResult.fiber = nutrientObject.value ?? null;
          break;
        case 2000:
          nutrientResult.sugars = nutrientObject.value ?? null;
          break;
        case 1003:
          nutrientResult.protein = nutrientObject.value ?? null;
          break;
        default:
          break;
      }
    });

    return nutrientResult;
  }

  static searchResultFood2IngredientProduct(usdaItem: SearchResultFood): IngredientProduct {
    const res: IngredientProduct = {} as IngredientProduct;
    res.fdcName = usdaItem.description;
    // composite name?
    res.fdcId = usdaItem.fdcId;
    res.ingredients = usdaItem.ingredients;
    res.urlSource = 'fdc api';
    res.nutrients = NutrientService.extractNutrientsFromLegacyAndFoundation(usdaItem);
    return res;
  }

  static matchGet2IngredientProduct(matchGet: IngredientMatchGetDto): IngredientProduct {
    const copy = _.cloneDeep(matchGet);
    const res: IngredientProduct = {} as IngredientProduct;
    res.compositeName = copy.compositeName;
    res.fdcId = copy.fdcId;
    if (copy.fdcName) {
      res.fdcName = copy.fdcName;
    }
    res.urlSource = 'server';
    delete copy.fdcId;
    delete copy.id;
    delete copy.fdcName;
    delete copy.sourceUrl;
    delete copy.compositeName;
    res.nutrients = { ...copy };
    return res;
  }

  static ingredientProduct2MatchGet(ingredientProduct: IngredientProduct): IngredientMatchGetDto {
    const copy = _.cloneDeep(ingredientProduct);
    const res: IngredientMatchGetDto = { ...ingredientProduct.nutrients } as IngredientMatchGetDto;

    res.compositeName = copy.compositeName;
    res.fdcName = copy.fdcName;
    res.fdcId = copy.fdcId;
    res.id = '';
    return res;
  }

  static nutritionSetTo100gram(nutritionSet: NutritionSetEditDto, servingSize: number): NutritionSetEditDto {
    Object.keys(nutritionSet).forEach(
      (key) =>
        (nutritionSet[key as keyof NutritionSetEditDto] = calcValueOfPercentage(
          nutritionSet[key as keyof NutritionSetEditDto],
          servingSize,
        )),
    );
    return nutritionSet;
  }

  static null2emptyStrings(object: any): any {
    const res = {};
    Object.keys(object).map((key: string) => {
      res[key] = object[key] ?? '';
    });
    return res;
  }

  static formik2ServerNutrients(values): Nutrients {
    const res = {} as Nutrients;
    Object.keys(values).map((key) => {
      res[key] = Number(values[key]) ?? null;
    });

    return res;
  }
}

export default NutrientService;
